import { UiMessage } from "@intelligems/ig-types";

type MessagePart = UiMessage["parts"][number];

const TAG = "<thinking>";
const COMPLETE = /<thinking>[\s\S]*?<\/thinking>/gi;
const OPEN_TO_END = /<thinking>[\s\S]*$/i;

/**
 * Hide the assistant's private `<thinking>` scratchpad from the merchant. The
 * model is instructed to do all diagnosis/planning inside a leading scratchpad
 * and emit only a short status outside it; this strips the scratchpad before
 * display.
 *
 * Handles the streaming case: while the (leading) scratchpad is still arriving
 * it has no closing tag yet, so an open block is suppressed through
 * end-of-string and a dangling partial open tag (`<`, `<thi`, ...) is trimmed —
 * nothing flashes before the closing tag lands.
 *
 * Returns a trimmed string so a scratchpad-only message collapses to `""`,
 * which the renderer treats as a non-renderable (no bubble).
 */
export function stripThinking(text: string): string {
  let out = text.replace(COMPLETE, "");
  out = out.replace(OPEN_TO_END, "");
  out = stripTrailingPartialTag(out);
  return out.trim();
}

function stripTrailingPartialTag(s: string): string {
  for (let n = Math.min(s.length, TAG.length - 1); n > 0; n--) {
    if (s.slice(-n) === TAG.slice(0, n)) {
      return s.slice(0, -n);
    }
  }
  return s;
}

/** Apply `strip` to every text part, dropping parts that collapse to empty. */
export function applyToTextParts(
  message: UiMessage,
  strip: (text: string) => string
): UiMessage {
  const parts: MessagePart[] = [];
  for (const part of message.parts) {
    if (part.type !== "text") {
      parts.push(part);
      continue;
    }
    const stripped = strip(part.text);
    if (stripped.length > 0) {
      parts.push({ ...part, text: stripped });
    }
  }

  return { ...message, parts };
}
